import { useEffect, useMemo, useState } from 'react'

import CsvIcon from '@mui/icons-material/ListAlt'
import { CircularProgress, Grid } from '@mui/material'
import {
  AutocompleteInput,
  BooleanField,
  Button,
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  Labeled,
  List,
  NumberField,
  RaRecord,
  ReferenceField,
  SearchInput,
  Show,
  ShowButton,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetList,
  useRecordContext,
  useTranslate,
} from 'react-admin'
import { useParams } from 'react-router-dom'

import { Tooltip } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { LXShowPeriodicRates } from '../../components/LXShowPeriodicRates'
import { ShowPrepayments } from '../../components/Prepayments/ShowPrepayments'
import { BeneficiaryHelper } from '../../helpers/Beneficiary.helper'
import { ProductsHelper } from '../../helpers/Products.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Prepayment } from '../../types/Prepayments'
import { ProductType } from '../../types/ProductType'
import { LXField } from './../../components/LXField'
import { LXImage } from './../../components/LXImage/LXImage'
import { API } from './../../helpers/Api.helper'
import styles from './Beneficiary.module.scss'
import { BeneficiaryStatementsShow } from './BeneficiaryShow/BeneficiaryStatementsShow'

export const BeneficiaryShow = (): JSX.Element => {
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const params = useParams()
  const userStore = useUserStore()

  const [beneficiaryName, setBeneficiaryName] = useState('')

  const prepaymentsQuery = useQuery<Prepayment[]>({
    queryKey: ['prepayments', params.id],
    queryFn: async () => {
      const response = await API.get<Prepayment[]>('/api/prepayments', {
        params: {
          targetType: 'beneficiary',
          targetId: params.id,
        },
      })

      return response.data
    },
    enabled: !!params.id,
  })

  // Get beneficiaryName for App title
  useEffect(() => {
    if (!params.id) {
      return
    }

    dataProvider
      .getOne('beneficiaries', { id: params.id as string })
      .then(res => setBeneficiaryName(res?.data.name ?? ''))
  }, [dataProvider, params?.id])

  const BeneficiariesShowActions = () => (
    <TopToolbar
      style={{
        paddingBottom: 0,
      }}
    >
      <Tooltip
        label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
          'pages.users.permissionsList.beneficiaries.edit',
        )}`}
        hidden={userStore.isAuthorised('beneficiaries.edit')}
      >
        <span>
          <EditButton disabled={!userStore.isAuthorised('beneficiaries.edit')} />
        </span>
      </Tooltip>
    </TopToolbar>
  )

  return (
    <Show
      className={styles.edit}
      title={`${translate('pages.beneficiaries._title')} / ${beneficiaryName}`}
      actions={<BeneficiariesShowActions />}
    >
      <TabbedShowLayout>
        <Tab label={translate('pages.beneficiaries._tabs.1')} contentClassName={styles.tab}>
          <BeneficiaryInformations />
        </Tab>
        {userStore.isAuthorised('products.read') && (
          <Tab
            label={translate('pages.beneficiaries._tabs.2')}
            path="products"
            contentClassName={styles.tab}
          >
            <BeneficiaryProducts beneficiaryId={params.id as string} />
          </Tab>
        )}
        {userStore.isAuthorised('statements.read') && (
          <Tab
            label={translate('pages.beneficiaries._tabs.3')}
            path="periodic-rates"
            contentClassName={styles.tab}
          >
            <LXShowPeriodicRates rateable="beneficiary" rateableId={params.id as string} />
          </Tab>
        )}
        {userStore.isAuthorised('statements.read') && (
          <Tab
            label={translate('pages.beneficiaries._tabs.4')}
            path="statements"
            contentClassName={styles.tab}
          >
            <BeneficiaryStatementsShow
              beneficiaryId={params.id as string}
              beneficiaryName={beneficiaryName}
            />
          </Tab>
        )}
        {(userStore.isAuthorised('beneficiaries.edit') ||
          (prepaymentsQuery.data?.length ?? 0) > 0) && (
          <Tab
            label={translate('pages.beneficiaries._tabs.5')}
            path="prepayments"
            contentClassName={styles.tab}
          >
            <ShowPrepayments targetType="beneficiary" targetId={params.id as string} />
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  )
}

const BeneficiaryInformations = () => {
  const translate = useTranslate()
  const record = useRecordContext()
  const userStore = useUserStore()

  const cmsList = useMemo(() => userStore.getCMSList(), [userStore])

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item lg={2}>
        <LXImage source="logoBase64" gridSize={12} />
      </Grid>
      <Grid container item direction="row" lg={10} spacing={2}>
        <LXField
          source="name"
          label={translate('_generics.name')}
          elementType={TextField}
          labeled
        />
        <LXField
          source="code"
          label={translate('_generics.code')}
          elementType={TextField}
          labeled
        />
        <Grid item lg={3}>
          <Labeled>
            <FunctionField
              source="cms"
              label={translate('pages.youtubeChannels.cms')}
              render={(record: RaRecord | undefined) =>
                cmsList.find(cms => cms.id === record?.cms)?.name ?? record?.cms
              }
            />
          </Labeled>
        </Grid>
        <LXField source="siret" elementType={NumberField} labeled />
        <LXField source="tvaNumber" elementType={NumberField} labeled />
        <LXField
          source="name"
          label={translate('pages.beneficiaries.beneficiaryType')}
          elementType={TextField}
          wrappingFunction={el => (
            <ReferenceField source="beneficiaryTypeId" reference="beneficiaryTypes" link={false}>
              {el}
            </ReferenceField>
          )}
          labeled
        />
        <LXField
          source="contactEmail"
          label={translate('pages.beneficiaries.contactEmail')}
          elementType={TextField}
          labeled
        />
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={2} />
      <Grid container item direction="row" lg={10} spacing={2}>
        <LXField
          source="dearPoliteForm"
          label={translate('pages.beneficiaries.dearPolite.title')}
          elementType={TextField}
          labeled
        />
        <LXField
          source="contactFirstName"
          label={translate('pages.beneficiaries.contactFirstName')}
          elementType={TextField}
          labeled
        />
        <LXField
          source="contactName"
          label={translate('pages.beneficiaries.contactName')}
          elementType={TextField}
          labeled
        />
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={2} />
      <Grid container item direction="row" lg={10} spacing={2}>
        <LXField
          source="address"
          label={translate('_generics.address')}
          elementType={TextField}
          labeled
        />
        <LXField
          source="address2"
          label={translate('_generics.address2')}
          elementType={TextField}
          labeled
        />
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={2} />
      <Grid container item direction="row" lg={10} spacing={2}>
        <LXField
          source="isAutoCorrected"
          label={translate('pages.beneficiaries.isAutoCorrected')}
          elementType={BooleanField}
          labeled
        />
        <LXField
          source="name"
          label={translate('pages.beneficiaries.defaultDatavizPreset')}
          elementType={TextField}
          size={6}
          wrappingFunction={el => (
            <ReferenceField
              source="defaultDatavizPresetId"
              reference="datavizPresets"
              link={(rec: any) => `/analytics/?presetId=${rec.defaultDatavizPresetId}`}
            >
              {el}
            </ReferenceField>
          )}
          labeled
        />
        {record.isAutoCorrected && (
          <>
            <LXField
              source="name"
              label={translate('pages.beneficiaries.defaultProductId')}
              elementType={TextField}
              size={6}
              wrappingFunction={el => (
                <ReferenceField source="defaultProductId" reference="products" link="show">
                  {el}
                </ReferenceField>
              )}
              labeled
            />
            <LXField
              source="name"
              label={translate('pages.beneficiaries.defaultProductTypeId')}
              elementType={TextField}
              size={6}
              wrappingFunction={el => (
                <ReferenceField source="defaultProductTypeId" reference="productTypes" link={false}>
                  {el}
                </ReferenceField>
              )}
              labeled
            />
          </>
        )}
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={2} />
      <Grid container item direction="row" lg={10} spacing={2}>
        <LXField
          source="mediaUploadGoogleDriveFolderId"
          label={translate('pages.beneficiaries.mediaUploadGoogleDriveFolderId')}
          elementType={TextField}
          labeled
        />
      </Grid>
    </Grid>
  )
}

const BeneficiaryProducts = ({ beneficiaryId }: { beneficiaryId: string }): JSX.Element => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)

  const { data } = useGetList<ProductType>('productTypes', {
    filter: {},
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  })

  const productTypes = useMemo(
    () => ProductsHelper.formatProductTypes(data ?? [], translate),
    [data, translate],
  )

  const handleCsvDownload = async (id: string) => {
    setIsDownloadingCsv(true)
    await BeneficiaryHelper.downloadBeneficiaryCatalogCsv(id)
    setIsDownloadingCsv(false)
  }

  const handleAssetsCsvDownload = async (id: string) => {
    setIsDownloadingCsv(true)
    await BeneficiaryHelper.downloadBeneficiaryAssetsCsv(id)
    setIsDownloadingCsv(false)
  }

  const BeneficiaryProductsActions = (): JSX.Element => (
    <TopToolbar>
      <Tooltip
        label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
          'pages.users.permissionsList.products.edit',
        )}`}
        hidden={userStore.isAuthorised('products.edit')}
      >
        <span>
          <CreateButton disabled={!userStore.isAuthorised('products.edit')} />
        </span>
      </Tooltip>
    </TopToolbar>
  )

  return (
    <>
      <Grid lg={12} container>
        <Grid
          lg={12}
          container
          alignItems="flex-end"
          justifyContent="flex-end"
          alignContent="flex-end"
        >
          <Button
            label={translate('pages.beneficiaries.downloadCatalogCsv')}
            onClick={() => handleCsvDownload(beneficiaryId)}
            disabled={isDownloadingCsv}
          >
            {isDownloadingCsv ? <CircularProgress size="1rem" /> : <CsvIcon />}
          </Button>
          <Button
            label={translate('pages.beneficiaries.downloadAssetsCsv')}
            onClick={() => handleAssetsCsvDownload(beneficiaryId)}
            disabled={isDownloadingCsv}
          >
            {isDownloadingCsv ? <CircularProgress size="1rem" /> : <CsvIcon />}
          </Button>
        </Grid>
      </Grid>
      <List
        resource="products"
        filter={{ beneficiaryId }}
        exporter={false}
        perPage={API.DEFAULT_PAGINATION.perPage}
        actions={<BeneficiaryProductsActions />}
        filters={[
          <SearchInput key="search" source="q" alwaysOn />,
          <AutocompleteInput
            key="productType"
            source="productTypeId"
            label="_generics.productType"
            choices={productTypes}
            groupBy={option => option.group}
            alwaysOn
          />,
        ]}
      >
        <Datagrid bulkActionButtons={false}>
          <TextField source="name" label={translate('_generics.name')} />
          <ReferenceField
            source="productTypeId"
            reference="productTypes"
            label={translate('pages.products.productType')}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            source="beneficiaryId"
            reference="beneficiaries"
            label={translate('_generics.beneficiary')}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
          <ShowButton />
        </Datagrid>
      </List>
    </>
  )
}
